import { SurveyUserRoles } from '@constants/user';
import { ColumnDef, Table } from '@g17eco/molecules/table';
import { getDefaultScope, getScopeModules } from '@utils/survey-scope';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown } from 'reactstrap';
import { useState } from 'react';
import { DeleteModal } from '@g17eco/molecules/confirm-modal';
import { SurveyWorkgroup } from '@g17eco/types/workgroup';
import { DataScopeAccess } from '@g17eco/types/surveyDataScope';
import { WorkgroupIcon } from '@g17eco/molecules/workgroup/WorkgroupIcon';
import { MetricGroup } from '@g17eco/types/metricGroup';
import { SurveyScope } from '@g17eco/types/surveyCommon';

interface WorkgroupEntity {
  _id: string;
  scope?: SurveyScope;
}

interface WorkgroupsTableHandlers {
  onDelete: (workgroupId: string) => Promise<void>;
}

interface Props<TEntity extends WorkgroupEntity> {
  entity: TEntity;
  workgroups: SurveyWorkgroup[];
  metricGroups: MetricGroup[];
  entityLabel: string;
  handlers: WorkgroupsTableHandlers;
  getCountDisplay: (
    workgroup: SurveyWorkgroup,
    role: SurveyUserRoles.Stakeholder | SurveyUserRoles.Verifier,
  ) => string | number;
  renderDelegateModal: (params: {
    isOpen: boolean;
    toggle: () => void;
    editingWorkgroup?: SurveyWorkgroup;
  }) => React.ReactNode;
}

export const WorkgroupsTableBase = <TEntity extends WorkgroupEntity>({
  entity,
  workgroups,
  metricGroups,
  entityLabel,
  handlers,
  getCountDisplay,
  renderDelegateModal,
}: Props<TEntity>) => {
  const [deleting, setDeleting] = useState<SurveyWorkgroup | undefined>();
  const [editing, setEditing] = useState<SurveyWorkgroup | undefined>();

  const handleDelete = () => {
    if (!deleting) {
      return;
    }

    handlers
      .onDelete(deleting._id)
      .finally(() => {
        setDeleting(undefined);
      });
  };

  const columns: ColumnDef<SurveyWorkgroup>[] = [
    {
      header: 'Workgroup',
      meta: {
        cellProps: {
          className: 'dont_translate',
        },
      },
      accessorKey: 'name',
      cell: ({ row }) => {
        const workgroup = row.original;
        return (
          <div>
            <WorkgroupIcon workgroup={workgroup} />
            {workgroup.name}
          </div>
        );
      },
    },
    {
      id: 'isAdmin',
      header: () => <i className='fal fa-user-gear text-ThemeIconSecondary' />,
      cell: ({ row }) => {
        const workgroup = row.original;
        return workgroup.permission.roles.includes(SurveyUserRoles.Admin) ? (
          <i className='fal fa-check text-ThemeSuccessMedium' />
        ) : (
          <i className='fal fa-xmark text-ThemeIconSecondary' />
        );
      },
    },
    {
      id: 'contributingMetricsCount',
      header: () => {
        return <i className='fa fa-user-edit text-primary' />;
      },
      accessorFn: (workgroup) => {
        return getCountDisplay(workgroup, SurveyUserRoles.Stakeholder);
      },
    },
    {
      id: 'verifyingMetricsCount',
      header: () => {
        return <i className='fa fa-user-check text-success' />;
      },
      accessorFn: (workgroup) => {
        return getCountDisplay(workgroup, SurveyUserRoles.Verifier);
      },
    },
    {
      id: 'modules',
      header: 'Modules',
      cell: ({ row }) => {
        const workgroup = row.original;
        const access = workgroup.permission.access;
        const scope =
          (access === DataScopeAccess.Full ? entity.scope : workgroup.permission.scope) ?? getDefaultScope();
        const modules = getScopeModules({ scope, metricGroups });

        switch (modules.length) {
          case 0:
            return '-';
          case 1:
            return modules[0].name;
          default:
            return (
              <SimpleTooltip text={modules.map((module) => module.name).join(', ')}>
                <i className='fal fa-info-circle text-ThemeIconSecondary' />
              </SimpleTooltip>
            );
        }
      },
    },
    {
      id: 'buttons',
      header: '',
      cell: (c) => (
        <UncontrolledDropdown>
          <DropdownToggle outline color='transparent' className='border-ThemeNeutralsLight px-2'>
            <i className={'fal fa-bars'}></i>
          </DropdownToggle>
          <DropdownMenu className='border-ThemeBorderDefault'>
            <DropdownItem onClick={() => setEditing(c.row.original)}>
              <i className='fal fa-pencil mr-2' />
              Edit
            </DropdownItem>
            <DropdownItem onClick={() => setDeleting(c.row.original)}>
              <i className='fal fa-trash mr-2 text-ThemeDangerMedium' />
              Delete
            </DropdownItem>
          </DropdownMenu>
        </UncontrolledDropdown>
      ),
    },
  ];

  return (
    <>
      <div className='mt-3'>
        <Table
          columns={columns}
          data={workgroups}
          responsive
          noData={`No workgroups have been delegated to this ${entityLabel}`}
        />
      </div>
      <DeleteModal
        title={`workgroup from ${entityLabel}`}
        content={
          <>
            <p>
              You are about to remove the workgroup <b>{deleting?.name}</b> from this {entityLabel}. This will remove{' '}
              {entityLabel} access to all users who have been delegated via this workgroup.
            </p>
            <p>Are you sure you want to remove this workgroup?</p>
          </>
        }
        isOpen={!!deleting}
        handleDelete={handleDelete}
        handleCancel={() => setDeleting(undefined)}
      />
      {renderDelegateModal({
        isOpen: !!editing,
        toggle: () => setEditing(undefined),
        editingWorkgroup: editing,
      })}
    </>
  );
};
